// FieldSkeleton.jsx
import React, { useEffect, useRef, useState } from "react";
import Skeleton from "@mui/material/Skeleton";

/**
 * FieldSkeleton
 * - If you pass `children`, it toggles between a debounced skeleton and the children.
 * - If you don't pass `children`, it just renders a skeleton (legacy behavior).
 *
 * Props:
 * - loading: boolean        -> when true, shows skeleton (debounced)
 * - showAfterMs: number     -> don't show skeleton unless loading lasts this long (default 100ms)
 * - minShowMs: number       -> once shown, keep skeleton up at least this long (default 300ms)
 * - inputHeight: number     -> skeleton height (default 38)
 * - animation: "wave"|false -> MUI animation (default "wave")

to avoid flashes of loader

 */
export default function FieldSkeleton({
                                          loading = true,
                                          showAfterMs = 100,
                                          minShowMs = 300,
                                          inputHeight = 38,
                                          animation = "wave",
                                          className,
                                          style,
                                          children,
                                      }) {
    const [visible, setVisible] = useState(false);
    const showTimer = useRef(null);
    const hideTimer = useRef(null);
    const shownAt = useRef(0);

    useEffect(() => {
        if (loading) {
            if (hideTimer.current) {
                clearTimeout(hideTimer.current);
                hideTimer.current = null;
            }
            if (visible) {
                if (!shownAt.current) shownAt.current = Date.now();
                return;
            }
            showTimer.current = setTimeout(() => {
                shownAt.current = Date.now();
                setVisible(true);
            }, showAfterMs);
        } else {
            if (showTimer.current) {
                clearTimeout(showTimer.current);
                showTimer.current = null;
            }
            if (!visible) return;

            const elapsed = Date.now() - (shownAt.current || 0);
            const remain = Math.max(0, minShowMs - elapsed);
            hideTimer.current = setTimeout(() => {
                setVisible(false);
                shownAt.current = 0;
            }, remain);
        }

        return () => {
            if (showTimer.current) clearTimeout(showTimer.current);
            if (hideTimer.current) clearTimeout(hideTimer.current);
        };
    }, [loading, visible, showAfterMs, minShowMs]);

    if (children !== undefined) {
        return visible ? (
            <Skeleton
                variant="rounded"
                height={inputHeight}
                animation={animation}
                className={className}
                style={style}
            />
        ) : (
            children
        );
    }

    return (
        <Skeleton
            variant="rounded"
            height={inputHeight}
            animation={animation}
            className={className}
            style={style}
        />
    );
}
